import { execSync } from 'node:child_process';
import { existsSync, mkdirSync } from 'node:fs';
import { builtinModules } from 'node:module';
import { basename, join, relative } from 'node:path';
import type { RollupNodeResolveOptions } from '@rollup/plugin-node-resolve';

/** The detected JavaScript runtime environment */
export type RuntimePlatform = 'node' | 'bun';

/**
 * The esbuild/bundler platform setting.
 * - 'node': Assumes Node.js environment, externalizes built-in modules
 * - 'browser': Assumes browser environment, polyfills Node APIs
 * - 'neutral': Runtime-agnostic, preserves all globals as-is (used for Bun)
 */
export type BundlerPlatform = 'node' | 'browser' | 'neutral';

/**
 * Get nodeResolve plugin options based on the target platform.
 *
 * For 'browser' platform (e.g., Cloudflare Workers), uses browser-compatible
 * export conditions so packages like the Cloudflare SDK resolve to their
 * web runtime instead of Node.js-specific code.
 *
 * For 'node' and 'neutral' (Bun) platforms, uses Node.js module resolution.
 */
export function getNodeResolveOptions(platform: BundlerPlatform): RollupNodeResolveOptions {
  if (platform === 'browser') {
    return {
      preferBuiltins: false,
      browser: true,
      exportConditions: ['browser', 'worker', 'default'],
    };
  }
  return {
    preferBuiltins: true,
    exportConditions: ['node'],
  };
}

/**
 * Detect the current JavaScript runtime environment.
 *
 * This is used by the bundler to determine the appropriate esbuild platform
 * setting. When running under Bun, we need to use 'neutral' platform to
 * preserve Bun-specific globals (like Bun.s3).
 */
export function detectRuntime(): RuntimePlatform {
  if (process.versions?.bun) {
    return 'bun';
  }
  return 'node';
}

/**
 * Whether the deployer should skip installing dependencies (and lockfile
 * generation) in the build output directory. Enabled by setting
 * MASTRA_BUILD_SKIP_INSTALL to "true" or "1". Useful for hermetic build
 * systems (e.g. Bazel) that supply node_modules externally and run in a
 * network-less sandbox where the output install would be redundant and fatal.
 */
export function shouldSkipInstall(): boolean {
  const value = process.env.MASTRA_BUILD_SKIP_INSTALL;
  return value === 'true' || value === '1';
}

export function upsertMastraDir({ dir = process.cwd() }: { dir?: string }) {
  const dirPath = join(dir, '.mastra');

  if (!existsSync(dirPath)) {
    mkdirSync(dirPath, { recursive: true });
    execSync(`echo ".mastra" >> .gitignore`);
  }
}

export function isDependencyPartOfPackage(dep: string, packageName: string) {
  if (dep === packageName) {
    return true;
  }

  return dep.startsWith(`${packageName}/`);
}

/**
 * Get the package name from a module ID
 */
export function getPackageName(id: string) {
  const parts = id.split('/');

  if (id.startsWith('@')) {
    return parts.slice(0, 2).join('/');
  }

  return parts[0];
}

/**
 * Check if an import specifier uses a protocol scheme rather than a package name.
 * Examples: `cloudflare:workers`, `data:text/javascript,...`, `node:fs`.
 */
export function hasImportProtocol(specifier: string): boolean {
  // Avoid treating Windows absolute paths like `C:\foo` as protocol imports.
  if (/^[A-Za-z]:[\\/]/.test(specifier)) {
    return false;
  }

  return /^[A-Za-z][A-Za-z\d+.-]*:/.test(specifier);
}

const DEFAULT_PROTOCOL_IMPORT_EXCLUDE_LIST = ['node:'] as const;

/**
 * Check if a specifier uses a non-builtin protocol that should be preserved at
 * runtime instead of being treated as an installable dependency.
 */
export function isExternalProtocolImport(
  specifier: string,
  excludeList: readonly string[] = DEFAULT_PROTOCOL_IMPORT_EXCLUDE_LIST,
): boolean {
  if (!hasImportProtocol(specifier)) {
    return false;
  }

  return !excludeList.some(prefix => specifier.startsWith(prefix));
}

function isRelativeImportSpecifier(specifier: string): boolean {
  return specifier === '.' || specifier === '..' || specifier.startsWith('./') || specifier.startsWith('../');
}

function isAbsolutePathSpecifier(specifier: string): boolean {
  return specifier.startsWith('/') || specifier.startsWith('\\\\') || /^[A-Za-z]:[\\/]/.test(specifier);
}

/**
 * During `mastra dev` we are compiling TS files to JS (inside workspaces) so that users can just their workspace packages.
 * We store these compiled files inside `node_modules/.cache` for each workspace package.
 */
export function getCompiledDepCachePath(rootPath: string, packageName: string) {
  return slash(join(rootPath, 'node_modules', '.cache', packageName));
}

/**
 * Convert windows backslashes to posix slashes
 *
 * @example
 * ```ts
 * slash('C:\\Users\\user\\code\\mastra') // 'C:/Users/user/code/mastra'
 * ```
 */
export function slash(path: string) {
  const isExtendedLengthPath = path.startsWith('\\\\?\\');

  if (isExtendedLengthPath) {
    return path;
  }

  return path.replaceAll('\\', '/');
}

/**
 * Make a Rollup-safe name: pathless, POSIX, and without parent/absolute segments
 */
export function rollupSafeName(name: string, rootDir: string) {
  const rel = relative(rootDir, name);
  let entry = slash(rel);
  entry = entry.replace(/^(\.\.\/)+/, '');
  entry = entry.replace(/^\/+/, '');
  entry = entry.replace(/^[A-Za-z]:\//, '');
  if (!entry) {
    entry = slash(basename(name));
  }
  return entry;
}

/**
 * Native binding loaders and infrastructure packages that should be ignored when identifying the actual package that requires native bindings
 */
const NATIVE_BINDING_LOADERS = [
  'node-gyp-build',
  'prebuild-install',
  'bindings',
  'node-addon-api',
  'node-pre-gyp',
  'nan', // Native Abstractions for Node.js
] as const;

/**
 * Finds the first real package from node_modules that likely contains native bindings, filtering out virtual modules and native binding loader infrastructure.
 *
 * @param moduleIds - Array of module IDs from a Rollup chunk
 * @returns The module ID of the actual native package, or undefined if not found
 *
 * @example
 * const moduleIds = [
 *   '\x00/path/node_modules/bcrypt/bcrypt.js?commonjs-module',
 *   '/path/node_modules/node-gyp-build/index.js',
 *   '/path/node_modules/bcrypt/bcrypt.js',
 * ];
 * findNativePackageModule(moduleIds); // Returns '/path/node_modules/bcrypt/bcrypt.js'
 */
export function findNativePackageModule(moduleIds: string[]): string | undefined {
  return moduleIds.find(id => {
    // Skip virtual modules (Rollup plugin-generated)
    if (id.startsWith('\x00')) {
      return false;
    }

    // Must be from node_modules
    if (!id.includes('/node_modules/')) {
      return false;
    }

    // Skip native binding loader infrastructure
    for (const loader of NATIVE_BINDING_LOADERS) {
      if (id.includes(`/${loader}/`) || id.includes(`/${loader}@`)) {
        return false;
      }
    }

    return true;
  });
}

/**
 * Ensures that server.studioBase is normalized:
 * - Adds leading slash if missing (e.g., 'admin' → '/admin')
 * - Removes trailing slashes (e.g., '/admin/' → '/admin')
 * - Normalizes multiple slashes to single slash (e.g., '//api' → '/api')
 * - Returns empty string for root paths ('/' or '')
 *
 * @param studioBase - The studioBase path to normalize
 * @returns Normalized studioBase path string
 * @throws Error if path contains invalid characters ('..', '?', '#')
 */
export function normalizeStudioBase(studioBase: string): string {
  studioBase = studioBase.trim();

  // Validate: no path traversal, no query params, no special chars
  if (studioBase.includes('..') || studioBase.includes('?') || studioBase.includes('#')) {
    throw new Error(`Invalid base path: "${studioBase}". Base path cannot contain '..', '?', or '#'`);
  }

  // Normalize multiple slashes to single slash
  studioBase = studioBase.replace(/\/+/g, '/');

  // Handle default value cases
  if (studioBase === '/' || studioBase === '') {
    return '';
  }

  // Remove trailing slash
  if (studioBase.endsWith('/')) {
    studioBase = studioBase.slice(0, -1);
  }

  // Add leading slash if missing
  if (!studioBase.startsWith('/')) {
    studioBase = `/${studioBase}`;
  }

  return studioBase;
}

/**
 * Configuration values for Studio's index.html placeholder injection.
 *
 * Each value is the **exact JavaScript expression** that replaces the
 * corresponding `'%%PLACEHOLDER%%'` token (including surrounding quotes).
 *
 * For literal strings pass `"'value'"` (quoted).
 * For runtime expressions pass the raw JS, e.g. `"window.location.hostname"`.
 */
export interface StudioInjectionConfig {
  host: string;
  port: string;
  protocol: string;
  apiPrefix: string;
  basePath: string;
  hideCloudCta: string;
  cloudApiEndpoint: string;
  experimentalFeatures: string;
  templates: string;
  telemetryDisabled: string;
  requestContextPresets: string;
  experimentalUI: string;
  agentSignals: string;
  signalsUI: string;
  organizationId: string;
  platformProjectId: string;
  platformObservabilityEndpoint: string;
  autoDetectUrl?: string;
  devServerInstanceId?: string;
}

/**
 * Replace all `%%MASTRA_*%%` placeholders in the Studio `index.html` with the
 * supplied configuration values.
 *
 * The `<base href>` tag and the `window.MASTRA_STUDIO_BASE_PATH` assignment
 * use `basePath` as a plain string (no surrounding quotes), while all other
 * placeholders replace `'%%TOKEN%%'` (with surrounding single-quotes in the
 * source HTML) with the provided expression verbatim.
 */
export function injectStudioHtmlConfig(html: string, config: StudioInjectionConfig): string {
  // `String.prototype.replace`/`replaceAll` treat `$` sequences ($$, $&, $`,
  // $', $n) in the replacement string as special patterns. Config values are
  // dynamic (e.g. request context presets), so use a replacement function to
  // insert them verbatim.
  const replace = (token: string, value: string) => {
    html = html.replace(token, () => value);
  };

  replace(`'%%MASTRA_DEV_SERVER_INSTANCE_ID%%'`, config.devServerInstanceId ?? "''");
  replace(`'%%MASTRA_SERVER_HOST%%'`, config.host);
  replace(`'%%MASTRA_SERVER_PORT%%'`, config.port);
  replace(`'%%MASTRA_SERVER_PROTOCOL%%'`, config.protocol);
  replace(`'%%MASTRA_API_PREFIX%%'`, config.apiPrefix);
  replace(`'%%MASTRA_HIDE_CLOUD_CTA%%'`, config.hideCloudCta);
  replace(`'%%MASTRA_CLOUD_API_ENDPOINT%%'`, config.cloudApiEndpoint);
  replace(`'%%MASTRA_EXPERIMENTAL_FEATURES%%'`, config.experimentalFeatures);
  replace(`'%%MASTRA_TEMPLATES%%'`, config.templates);
  replace(`'%%MASTRA_TELEMETRY_DISABLED%%'`, config.telemetryDisabled);
  replace(`'%%MASTRA_REQUEST_CONTEXT_PRESETS%%'`, config.requestContextPresets);
  replace(`'%%MASTRA_EXPERIMENTAL_UI%%'`, config.experimentalUI);
  replace(`'%%MASTRA_AGENT_SIGNALS%%'`, config.agentSignals);
  replace(`'%%MASTRA_SIGNALS_UI%%'`, config.signalsUI);
  replace(`'%%MASTRA_ORGANIZATION_ID%%'`, config.organizationId);
  replace(`'%%MASTRA_PLATFORM_PROJECT_ID%%'`, config.platformProjectId);
  replace(`'%%MASTRA_PLATFORM_OBSERVABILITY_ENDPOINT%%'`, config.platformObservabilityEndpoint);
  if (config.autoDetectUrl) {
    replace(`'%%MASTRA_AUTO_DETECT_URL%%'`, config.autoDetectUrl);
  }
  html = html.replaceAll('%%MASTRA_STUDIO_BASE_PATH%%', () => config.basePath);

  return html;
}

/**
 * Escape a dynamic value for embedding inside a single-quoted JavaScript
 * string literal in the Studio `index.html` (e.g. `window.X = '<value>'`).
 * Without it, an env-derived value containing `'` or `</script>` breaks out
 * of the literal and corrupts (or injects into) the served page.
 */
export function escapeStudioHtmlValue(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/'/g, "\\'")
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '\\r')
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');
}

/**
 * Check if a module is a Node.js builtin module
 * @param specifier - Module specifier
 * @returns True if it's a builtin module
 */
export function isBuiltinModule(specifier: string): boolean {
  return (
    builtinModules.includes(specifier) ||
    specifier.startsWith('node:') ||
    builtinModules.includes(specifier.replace(/^node:/, ''))
  );
}

/**
 * Check whether a module specifier is a bare module import rather than a path,
 * virtual module, or Node builtin.
 */
export function isBareModuleSpecifier(specifier: string): boolean {
  if (!specifier || specifier.startsWith('#')) {
    return false;
  }

  if (isRelativeImportSpecifier(specifier) || isAbsolutePathSpecifier(specifier)) {
    return false;
  }

  if (isBuiltinModule(specifier)) {
    return false;
  }

  if (isExternalProtocolImport(specifier)) {
    return false;
  }

  return true;
}
